<template>
  <q-dialog
    :model-value="modelValue"
    class="kds-changelog-modal"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="titleId"
    :transition-show="dialogTransition"
    :transition-hide="dialogTransition"
    @update:model-value="emit('update:modelValue', $event)"
    @hide="onHide"
  >
    <div
      class="kds-changelog-modal__container"
      :class="{ 'kds-changelog-modal__container--reduced': prefersReducedMotion }"
    >
      <!-- Sticky header -->
      <header class="kds-changelog-modal__header">
        <div class="kds-changelog-modal__title-row">
          <KdsEyebrow :id="titleId">{{ t('changelogs.modal.title') }}</KdsEyebrow>
          <span
            v-if="currentVersion"
            class="kds-changelog-modal__version font-mono"
          >
            {{ t('changelogs.modal.versionEyebrow', { version: currentVersion }) }}
          </span>
        </div>
        <button
          type="button"
          class="kds-changelog-modal__close"
          :aria-label="t('changelogs.modal.closeAria')"
          @click="emit('update:modelValue', false)"
        >
          <KdsIcon name="x" :size="18" />
        </button>
      </header>

      <!-- Scrollable body -->
      <div class="kds-changelog-modal__body">
        <!-- Empty state -->
        <div v-if="versions.length === 0" class="kds-changelog-modal__empty">
          <KdsIcon name="scroll" :size="32" class="kds-changelog-modal__empty-icon" />
          <p class="kds-changelog-modal__empty-text">{{ t('changelogs.modal.empty') }}</p>
        </div>

        <!-- Changelog entries — most recent first (versions already sorted desc in useChangelog).
             Staggered entrance (≤ 8 items) via TransitionGroup + --i on each <li> (D2). -->
        <TransitionGroup
          v-else
          name="stagger"
          tag="ul"
          class="kds-changelog-modal__list"
          role="list"
        >
          <li
            v-for="(entry, idx) in versions"
            :key="entry.version"
            class="kds-changelog-modal__entry"
            :style="{ '--i': Math.min(idx, 8) }"
          >
            <KdsChangelogEntryCard
              :version="entry.version"
              :type="entry.type"
              :category="entry.category"
              :date="entry.date"
              :title="entry.title"
              :summary="entry.summary"
              :highlights="entry.highlights"
              :image-url="entry.imageUrl"
              :image-alt="entry.imageAlt"
              :index="idx"
            />
          </li>
        </TransitionGroup>
      </div>
    </div>
  </q-dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import KdsEyebrow from '@/kds/primitives/KdsEyebrow.vue';
import KdsIcon from '@/kds/primitives/KdsIcon.vue';
import KdsChangelogEntryCard from './KdsChangelogEntryCard.vue';
import { useChangelog } from '@/composables/useChangelog';
import { useIsMobile } from '@/composables/useIsMobile';

// Stable id for aria-labelledby linkage
const titleId = 'kds-changelog-modal-title';

const props = defineProps<{
  /** Controls dialog open/close state (v-model). */
  modelValue: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'hide': [];
}>();

void props;

const { t } = useI18n();
const { versions, currentVersion } = useChangelog();
const isMobile = useIsMobile();

const prefersReducedMotion = computed<boolean>(() => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
});

// D3: fade on desktop, slide-up on mobile (slide-up matches the bottom-anchored
// mobile layout). Quasar handles the reverse direction automatically on hide.
const dialogTransition = computed<string>(() => (isMobile.value ? 'slide-up' : 'fade'));

const onHide = (): void => {
  emit('update:modelValue', false);
  emit('hide');
};
</script>

<style lang="scss">
/* Non-scoped so the q-dialog overlay is reachable */
.kds-changelog-modal {
  .q-dialog__inner {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: var(--space-6);

    @media (max-width: 479px) {
      padding: 0;
      align-items: flex-end;
    }
  }
}
</style>

<style scoped lang="scss">
.kds-changelog-modal {
  &__container {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 720px;
    max-height: 85dvh;
    background: var(--paper);
    border-radius: var(--r-5);
    border: var(--border);
    box-shadow: var(--shadow-3);
    overflow: hidden;

    // Slide-up on mobile
    @media (max-width: 479px) {
      max-width: 100vw;
      max-height: 100dvh;
      border-radius: 0;
      border-left: none;
      border-right: none;
      border-bottom: none;
    }
  }

  &__header {
    position: sticky;
    top: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-4);
    padding: var(--space-5) var(--space-6);
    background: var(--paper);
    border-bottom: var(--border);
    flex-shrink: 0;
  }

  &__title-row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  &__version {
    font-family: var(--font-mono);
    font-size: var(--fs-mono);
    color: var(--stone);
    letter-spacing: 0.04em;
  }

  &__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    color: var(--stone);
    cursor: pointer;
    border-radius: var(--r-2);
    flex-shrink: 0;
    transition: color var(--d-fast) var(--ease-out), background-color var(--d-fast) var(--ease-out);

    &:hover {
      color: var(--ink);
      background: var(--paper-3);
    }

    &:focus-visible {
      outline: none;
      box-shadow: var(--shadow-focus);
    }
  }

  &__body {
    flex: 1;
    overflow-y: auto;
    padding: var(--space-6);
    display: flex;
    flex-direction: column;
    gap: var(--space-5);

    @media (max-width: 479px) {
      padding: var(--space-5);
    }
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
  }

  &__entry {
    // li wrapper — no extra styling; KdsChangelogEntryCard fills width
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-4);
    padding: var(--space-9) 0;
    text-align: center;
  }

  &__empty-icon {
    color: var(--stone-2);
  }

  &__empty-text {
    margin: 0;
    font-size: var(--fs-body);
    color: var(--stone);
  }
}

// Stagger entrance transition — matches VerdictsListPage / ux.md (200ms item, 50ms step).
// --i is set on each <li>; capped at 8 to bound the max delay (D2).
.stagger-enter-active {
  transition:
    opacity var(--d-base) var(--ease-out),
    transform var(--d-base) var(--ease-out);
  transition-delay: calc(var(--i, 0) * 50ms);
}
.stagger-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.stagger-leave-active {
  transition: opacity 150ms var(--ease-out);
  position: absolute;
}
.stagger-leave-to {
  opacity: 0;
}
.stagger-move {
  transition: transform var(--d-base) var(--ease-out);
}

// Reduced motion: the container carries the --reduced modifier; also honour the
// system media query so the staggered entrance collapses to an instant render.
.kds-changelog-modal__container--reduced {
  .stagger-enter-active,
  .stagger-leave-active,
  .stagger-move {
    transition: none;
  }
  .stagger-enter-from {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .stagger-enter-active,
  .stagger-leave-active,
  .stagger-move {
    transition: none;
  }
  .stagger-enter-from {
    opacity: 1;
    transform: none;
  }
}
</style>
